import { createFileRoute, Link } from "@tanstack/react-router";
import { LoanCategoryPage } from "@/components/laneguide/LoanCategoryPage";
import { PAGE_FAQ } from "@/components/laneguide/PageFaq";

const SITE = "https://svensklaneguide.se";
const URL = `${SITE}/snabblan-1000-kr`;
const TITLE = "Snabblån Från 1000kr – jämför små snabblån | Låneguide";
const DESC =
  "Snabblån från 1000 kr med utbetalning direkt. Jämför långivare som beviljar små belopp, utan UC och trots betalningsanmärkning. Se topplistan i lugn och ro.";

export const Route = createFileRoute("/snabblan-1000-kr")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Snabblån Från 1000kr – jämför små snabblån" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Snabblån Från 1000kr" },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: SITE },
            { "@type": "ListItem", position: 2, name: "Snabblån", item: `${SITE}/snabblan` },
            { "@type": "ListItem", position: 3, name: "Snabblån från 1000 kr", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: (PAGE_FAQ["snabblan"] ?? []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: () => (
    <LoanCategoryPage
      page="snabblan-1000"
      productLabel="Snabblån från 1 000 kr"
      headingBefore="Snabblån"
      underlined="Från 1000kr"
      headingAfter=""
      subtitle={
        <>
          Behöver du bara en mindre summa snabbt? Vi förstår – alla behov är inte stora. Här listar vi
          långivare som beviljar{" "}
          <Link to="/nyhet/$slug" params={{ slug: "snabblan-fran-1000-kr" }} className="text-primary underline underline-offset-2">
            snabblån från 1 000 kr
          </Link>{" "}
          med utbetalning direkt, ofta utan UC och i vissa fall trots betalningsanmärkning. Långivarna med
          allra lägst belopp ligger högst upp – därefter följer aktörer som startar på 2 000–3 000 kr.
        </>
      }
      featuredTitleTemplate={(m) => `Toppval för små snabblån i ${m}`}
      belowList={<BelowContent />}
    />
  ),
});

function BelowContent() {
  return (
    <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-border bg-card p-6 sm:p-10">
        <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Snabblån från 1 000 kr – så väljer du rätt
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground/80">
          Att låna en tusenlapp känns ofta större än det är. Vi tänker inte moralisera – de flesta som lånar
          små belopp gör det för att en räkning måste betalas innan förfallodagen. Däremot tänker vi vara
          ärliga: på små lån är det avgifterna, inte räntan, som avgör vad du faktiskt betalar.
        </p>

        <div className="mt-10 space-y-8">
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Varför färre långivare erbjuder 1 000 kr
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Kreditprövningen kostar lika mycket för långivaren oavsett om du lånar 1 000 eller 100 000 kr.
              Därför har flera aktörer höjt sitt lägsta belopp. De som fortfarande lånar ut riktigt små summor
              gör det oftast via en kontokredit, där du beviljas ett utrymme och själv väljer hur mycket du
              plockar ut.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Räkna på kronor – inte bara på procent
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Effektiv ränta räknas om till årsbasis och ser därför dramatisk ut på ett lån som löper i två
              månader. Titta i stället på hur många kronor du betalar tillbaka totalt. En uppläggningsavgift på
              195 kr på ett lån om 1 000 kr väger tyngre än flera procentenheters ränteskillnad.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Utbetalning direkt – vad som faktiskt styr hastigheten
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Pengarna går snabbast när långivaren använder realtidsbetalning eller när ni delar bank. Då kan
              beloppet vara framme inom cirka 15 minuter, även på kvällar och helger. Går utbetalningen via
              vanlig bankclearing bokförs pengarna först nästa bankdag.
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">Utan UC och med anmärkning</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Flera av långivarna i listan gör kreditprövningen via Creditsafe eller Bisnode i stället för UC.
              Kreditprövning sker alltid enligt lag, men ansökan syns inte i UC:s register. Har du en äldre
              anmärkning gör vissa aktörer en individuell bedömning där din nuvarande inkomst väger tyngre än
              historiken. Se även{" "}
              <Link to="/lan-utan-uc" className="text-primary underline underline-offset-2">
                lån utan UC
              </Link>{" "}
              och{" "}
              <Link to="/mikrolan-utan-uc-1000-kr" className="text-primary underline underline-offset-2">
                mikrolån utan UC från 1 000 kr
              </Link>
              .
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Kostnadstaket skyddar dig
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Enligt konsumentkreditlagen får de samlade kostnaderna för ränta, avgifter och dröjsmålsränta
              aldrig överstiga det ursprungliga lånebeloppet. Lånar du 1 000 kr kan skulden alltså aldrig växa
              förbi 2 000 kr. Mer om reglerna finns hos{" "}
              <a
                href="https://www.konsumentverket.se"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                Konsumentverket
              </a>
              .
            </p>
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-foreground">
              Har du redan flera små krediter?
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
              Då är ytterligare ett smålån sällan rätt väg. Ett{" "}
              <Link to="/samla-lan" className="text-primary underline underline-offset-2">
                samlingslån
              </Link>{" "}
              ger dig en avi, en ränta och ett tydligt slutdatum – och blir nästan alltid billigare totalt.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
